// Cancelling and deleting a registration — the dialogs and the shared logic,
// used by both the Website Signups table and one signup's detail page.
//
// The two actions are deliberately not variations of each other:
//
//   Cancel keeps the record. The person stays in the list with a Cancelled
//   status, their notes and consent evidence are untouched, their WS id is
//   unchanged, and the seat goes back to the event.
//
//   Delete removes the personal data for good. It is full-admin only, it makes
//   the administrator retype the WS registration id, and it says plainly that
//   there is no undo.
//
// Neither emails anyone. Telling a registrant is a separate decision.

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import { displayValue, fullName, signupStatusMeta } from '@/lib/websiteEngagement';
import { AlertTriangle, Loader2 } from 'lucide-react';

const inputClass = adminInputClass;

/** Case-insensitive so "ws-2026-000001" typed in a hurry still matches. */
export function confirmationMatches(typed, expected) {
  const wanted = String(expected || '').trim().toUpperCase();
  return !!wanted && String(typed || '').trim().toUpperCase() === wanted;
}

export function CancelRegistrationDialog({ open, registration, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open, registration?.id]);
  if (!registration) return null;

  const alreadyCancelled = registration.status === 'cancelled';
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="cancel-registration-dialog">
        <DialogHeader>
          <DialogTitle>Cancel this registration?</DialogTitle>
          <DialogDescription>
            {alreadyCancelled
              ? 'This registration is already cancelled. Cancelling again changes nothing.'
              : 'The record stays here with a Cancelled status. Notes, mention history and '
                + 'consent evidence are kept, the registration ID does not change, and the seat '
                + 'goes back to the event. Nobody is emailed.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] p-3">
            <p className="font-semibold text-[#062B1A]">{displayValue(fullName(registration))}</p>
            <p className="font-mono text-xs text-[#062B1A]/60">{registration.registration_id}</p>
            <p className="text-xs text-[#062B1A]/60">
              {displayValue(registration.event_title)} · {signupStatusMeta(registration.status).label}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason">Reason (optional)</Label>
            <Textarea
              id="cancel-reason"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Kept in the audit log, not sent to anyone."
              className={inputClass}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
            Keep it
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => onConfirm({ reason })}
            className="bg-[#8A6508] text-white hover:bg-[#6d5006]"
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Cancel registration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteRegistrationDialog({
  open, registration, eventReference, busy, onCancel, onConfirm,
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) { setTyped(''); setReason(''); }
  }, [open, registration?.id]);
  if (!registration) return null;

  const expected = registration.registration_id || '';
  const confirmed = confirmationMatches(typed, expected);
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="delete-registration-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[rgb(153,27,27)]">
            <AlertTriangle className="size-5" /> Permanently delete this registration?
          </DialogTitle>
          <DialogDescription>
            This removes the registrant’s personal data for good. It cannot be undone, and the
            registration ID is never reused.
          </DialogDescription>
        </DialogHeader>

        <dl className="space-y-2 rounded-lg border border-[rgba(153,27,27,0.25)] bg-[rgba(254,242,242,0.7)] p-3 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-[#062B1A]/60">Registrant</dt>
            <dd className="font-semibold text-[#062B1A]">{displayValue(fullName(registration))}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-[#062B1A]/60">Registration ID</dt>
            <dd className="font-mono text-xs text-[#062B1A]">{displayValue(expected)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-[#062B1A]/60">Event</dt>
            <dd className="text-right text-[#062B1A]">{displayValue(registration.event_title)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-[#062B1A]/60">Staff event reference</dt>
            <dd className="text-right font-mono text-xs text-[#062B1A]">
              {displayValue(eventReference)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-[#062B1A]/60">Current status</dt>
            <dd className="text-[#062B1A]">{signupStatusMeta(registration.status).label}</dd>
          </div>
        </dl>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="delete-confirm">
              Type <span className="font-mono">{expected}</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              aria-label="Type the registration ID to confirm"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="delete-reason">Reason</Label>
            <Input
              id="delete-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Test submission, spam, duplicate, privacy request…"
              className={inputClass}
            />
            <p className="text-xs text-[#062B1A]/45">
              Only the registration ID, the event, your admin ID, this reason and the time are
              kept. No name, email, phone or message is stored in the audit record.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
            Keep it
          </Button>
          <Button
            type="button"
            disabled={!confirmed || busy}
            onClick={() => onConfirm({ confirm_registration_id: typed.trim(), reason })}
            className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,22,22)]"
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDeleteRegistrationsDialog({ open, registrations, busy, onCancel, onConfirm }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState('');
  const rows = Array.isArray(registrations) ? registrations : [];
  useEffect(() => {
    if (open) { setAcknowledged(false); setReason(''); }
  }, [open, rows.length]);
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[rgb(153,27,27)]">
            <AlertTriangle className="size-5" />
            Permanently delete {rows.length} selected registration{rows.length === 1 ? '' : 's'}?
          </DialogTitle>
          <DialogDescription>
            Only the {rows.length} record{rows.length === 1 ? '' : 's'} you ticked will be deleted —
            not everything matching the current filters. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-40 overflow-y-auto rounded-lg border border-[rgba(153,27,27,0.25)] bg-[rgba(254,242,242,0.7)] p-3">
          <ul className="space-y-1 font-mono text-xs text-[#062B1A]">
            {rows.map((row) => (
              <li key={row.id} data-testid="bulk-delete-id">
                {row.registration_id || row.id}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-delete-reason">Reason</Label>
            <Input
              id="bulk-delete-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Test submissions, spam, duplicates…"
              className={inputClass}
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-[#062B1A]">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              aria-label="Confirm the bulk deletion"
              className="mt-1 size-4 accent-[rgb(153,27,27)]"
            />
            <span>
              I understand these {rows.length} registration{rows.length === 1 ? '' : 's'} will be
              permanently deleted.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
            Keep them
          </Button>
          <Button
            type="button"
            disabled={!acknowledged || busy || rows.length === 0}
            onClick={() => onConfirm({ reason })}
            className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,22,22)]"
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Delete {rows.length} permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
